// Estado de sesión: perfil del usuario actual o null si no hay sesión.
//
// La verdad operativa vive en la cookie HttpOnly del backend; aquí solo
// cacheamos el `user` (con role) para pintar nombre y para que el router
// decida si redirigir a /login o /admin según permisos.
//
// Sub-fase A: el backend ya no usa Dolibarr para login. La respuesta de
// /auth/login puede incluir `must_set_password: true` — en ese caso NO se
// emite cookie y el caller debe llevar al usuario a /set-password.

import { ApiError, apiGet, apiPost } from '../api/client.js';

let _user = null;
const _listeners = new Set();

function _notify() {
  for (const l of _listeners) {
    try { l(_user); } catch (e) { console.error('auth listener falló', e); }
  }
}

export function getUser() { return _user; }
export function isAdmin() { return _user?.role === 'admin'; }

export function subscribe(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/** Pregunta a /auth/me. Cachea el resultado (o null) en `_user`. */
export async function checkSession() {
  try {
    _user = await apiGet('/auth/me');
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      _user = null;
    } else {
      console.warn('checkSession falló:', e);
    }
  }
  _notify();
  return _user;
}

/** Hace login. Devuelve la respuesta cruda: o bien `{must_set_password:true, username}`
 *  (sin sesión emitida — el caller debe redirigir a /set-password), o bien el
 *  UserPublic con role (sesión activa). */
export async function login(username, password) {
  const res = await apiPost('/auth/login', { username, password });
  if (res?.must_set_password) {
    _user = null;
    _notify();
    return res;
  }
  _user = {
    user_id: res.user_id,
    username: res.username,
    fullname: res.fullname,
    role: res.role,
  };
  _notify();
  return res;
}

/** Establece la contraseña por primera vez. Tras éxito emite cookie y
 *  el usuario queda autenticado. */
export async function setPassword(username, password) {
  const res = await apiPost('/auth/set-password', { username, password });
  _user = {
    user_id: res.user_id,
    username: res.username,
    fullname: res.fullname,
    role: res.role,
  };
  _notify();
  return _user;
}

export async function logout() {
  try { await apiPost('/auth/logout'); }
  catch { /* ignorar — la cookie se borra del lado del servidor */ }
  _user = null;
  _notify();
}

window.addEventListener('auth:expired', () => {
  if (_user !== null) { _user = null; _notify(); }
});
